import { ethers } from "ethers";

import {
  challengeContractAddress,
  getContract,
  getMetaMaskSigner,
  getReadOnlyContract,
  governanceContractAddress,
} from "./contract.service";
import AxiosFactory from "./http.service";
import ApiRoutes from "../config/api.routes";
import { MainChainID } from "../utils/constants";
import { decodeRelayRevert } from "./relay.service";
import v10ErrorSelectors from "../data/v10-error-selectors.json";

// V10.1 adjudication client — EcoChallenge (deed + verification stage) and Governance (pre-mint).
//
// DIRECT WALLET TRANSACTIONS TODAY — BY CHOICE, NO LONGER BY NECESSITY. Up to V10.1.1 EcoChallenge
// read raw `msg.sender`, so a call through EcoForwarder arrived as the *forwarder*: raiseChallenge
// failed `_requireIndependent(forwarder)` and nothing here could be relayed. Since V10.1.2
// EcoChallenge reads the relayed signer, and the backend's relay allowlist already carries
// attest / raiseChallenge / respondToChallenge / withdrawChallenge (target "challenge"), so those
// four COULD go gasless (EcoChallenge guide §3e). They still go out as direct wallet transactions
// here — the caller pays gas — until that switch is made deliberately. recordOutcome never needs
// the relay at all: it has no sender check, the panel's signatures are the authority, and since
// V10.1.2 the server collects them and submits it (see "Server-collected verdicts" below).
// Governance still reads raw msg.sender, so judgePreMint stays direct. Core's pre-mint
// `challenge` / `respondToChallenge` use `_msgSender()` and stay on the relay.
//
// The panel never writes a score (V10.1). It records two independent booleans and the contract
// derives the consequences:
//   upheld   — governs the VERIFIER: false flags the challenger; true flags the verifier (and the
//              standing attestor), and on a FIELD finding moves the seat.
//   inBreach — governs the COVENANT: freezes the TREE drip until a real re-verification.
// All four combinations are meaningful; "not upheld, in breach" is the verifier being right about
// land that is genuinely breached.
//
// Authority is 2-of-3 EIP-712 panel signatures (or 1-of-1 for a single checker), verified on chain
// with SigLib.countSignatures. Anyone holding enough signatures may submit — normally the backend,
// which collects them per seat (POST /challenges/:id/verdict-signatures). Two traps:
//   - the digest includes a per-challenge nonce that increments on every determination, so a
//     signature built against a stale nonce is not "expired" — it silently fails the count and
//     surfaces as InsufficientPanelSignatures. signVerdict re-reads the nonce every time.
//   - SigLib requires signatures ordered by signer address, strictly ascending. orderSignatures
//     sorts (and dedupes) before submission; getting this wrong reverts SignersNotAscending.

// EcoChallenge.Kind — 0 is NONE and reverts InvalidKind.
export enum ChallengeKind {
  EVIDENCE = 1,
  FIELD = 2,
}

// EcoChallenge.State
export enum ChallengeState {
  NONE = 0,
  OPEN = 1,
  RESPONDED = 2,
  SEATED = 3,
  DETERMINED = 4,
  DISMISSED_BY_LAPSE = 5,
}

// EcoChallenge.WindowKind / WindowAction
export enum WindowKind {
  NONE = 0,
  VERIFICATION = 1,
  DEED = 2,
}
export enum WindowAction {
  NONE = 0,
  ATTESTED = 1,
  CHALLENGED = 2,
  LAPSED = 3,
}

// Governance.Ground — the README says "0 = SCORE, 1 = DOCUMENTS"; the SOURCE says
// enum Ground { NONE, SCORE, DOCUMENTS } and NONE reverts PreMintScore. Trust the source.
export enum PreMintGround {
  SCORE = 1,
  DOCUMENTS = 2,
}

// Core.challenge(requestId, evidence) takes no ground — the panel names it in judgePreMint — so the
// challenger's choice rides at the head of the evidence as a tag in the judges' own words. Written
// by the verification page when the challenge is raised; read back to preselect the judge's ground.
export const PRE_MINT_GROUND_TAG: Record<PreMintGround, string> = {
  [PreMintGround.SCORE]: "[Score]",
  [PreMintGround.DOCUMENTS]: "[Documents]",
};

export const preMintGroundFromEvidence = (evidence?: string | null): PreMintGround | null => {
  const head = evidence?.trimStart() ?? "";
  if (head.startsWith(PRE_MINT_GROUND_TAG[PreMintGround.SCORE])) {
    return PreMintGround.SCORE;
  }
  if (head.startsWith(PRE_MINT_GROUND_TAG[PreMintGround.DOCUMENTS])) {
    return PreMintGround.DOCUMENTS;
  }
  return null;
};

export interface ChallengeRecord {
  challengeId: bigint;
  subject: bigint;
  challenger: string;
  verifier: string;
  kind: ChallengeKind;
  submittedScore: number;
  // true = a panel of three decides by two; false = one checker (panel[0]) decides alone.
  panelPath: boolean;
  upheld: boolean;
  inBreach: boolean;
  redrawn: boolean;
  heldSettlement: boolean;
  state: ChallengeState;
  openedAt: Date | null;
  respondedAt: Date | null;
  seatedAt: Date | null;
  panel: string[];
}

export interface ReviewWindow {
  kind: WindowKind;
  action: WindowAction;
  actor: string;
  attestor: string;
  openedAt: Date | null;
  closesAt: Date | null;
  challengeId: bigint;
}

export const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

const toDate = (v: bigint | number): Date | null => (Number(v) > 0 ? new Date(Number(v) * 1000) : null);

const parseChallenge = (challengeId: bigint, c: any): ChallengeRecord => ({
  challengeId,
  subject: BigInt(c.subject),
  challenger: String(c.challenger),
  verifier: String(c.verifier),
  kind: Number(c.kind) as ChallengeKind,
  submittedScore: Number(c.submittedScore),
  panelPath: Boolean(c.panelPath),
  upheld: Boolean(c.upheld),
  inBreach: Boolean(c.inBreach),
  redrawn: Boolean(c.redrawn),
  heldSettlement: Boolean(c.heldSettlement),
  state: Number(c.state) as ChallengeState,
  openedAt: toDate(c.openedAt),
  respondedAt: toDate(c.respondedAt),
  seatedAt: toDate(c.seatedAt),
  // The struct always carries three slots; unfilled ones are the zero address.
  panel: Array.from(c.panel as string[]).map(String),
});

const challengeReader = () => {
  const c = getReadOnlyContract("challenge");
  if (!c) {
    throw new Error("EcoChallenge contract not found");
  }
  return c;
};

const governanceReader = () => {
  const g = getReadOnlyContract("governance");
  if (!g) {
    throw new Error("Governance contract not found");
  }
  return g;
};

const coreReader = () => {
  const c = getReadOnlyContract("ecofutures-core");
  if (!c) {
    throw new Error("EcoFuturesCore contract not found");
  }
  return c;
};

// Whether a pre-mint challenge is currently open on this request — flips false the moment the
// server's judgePreMint lands, which is the signal the server-collected lane waits on (there is no
// SEATED/DETERMINED trail on the pre-mint side to poll instead, unlike EcoChallenge).
export const isPreMintChallenged = async (requestId: bigint | number | string): Promise<boolean> =>
  Boolean(await coreReader().isChallenged(BigInt(requestId)));

export const readChallenge = async (challengeId: bigint | number | string): Promise<ChallengeRecord> => {
  const id = BigInt(challengeId);
  const raw = await challengeReader().getChallenge(id);
  return parseChallenge(id, raw);
};

export const readWindow = async (tokenId: bigint | number | string): Promise<ReviewWindow> => {
  const w = await challengeReader().windowOf(BigInt(tokenId));
  return {
    kind: Number(w.kind) as WindowKind,
    action: Number(w.action) as WindowAction,
    actor: String(w.actor),
    attestor: String(w.attestor),
    openedAt: toDate(w.openedAt),
    closesAt: toDate(w.closesAt),
    challengeId: BigInt(w.challengeId),
  };
};

// Issue #628: V10.1.2's raiseChallenge never checks that a window is open. One raised outside a
// window is still accepted — it takes no window, cannot hold a settlement, resets nothing, and is
// how several challenges against one verification each add a flag. The next contract release
// reverts it (NoOpenWindow / WindowClosed / WindowAlreadyTaken, as attest does); until then the
// UI only offers Challenge while this holds, which is exactly that future contract rule:
// a window exists, the chain clock has not passed closesAt, and nobody has challenged it or let
// it lapse (ATTESTED is fine — a challenge supersedes an attestation in the same window).
// `nowMs` is chain time, not the wall clock — see chainClockOffsetMs in useCovenantReviewState.
export type ChallengeWindowBlock = "none" | "closed" | "taken" | "lapsed";

export const challengeWindowBlock = (
  w: ReviewWindow | null | undefined,
  nowMs: number
): ChallengeWindowBlock | null => {
  if (!w || w.kind === WindowKind.NONE) {
    return "none";
  }
  if (w.action === WindowAction.CHALLENGED) {
    return "taken";
  }
  if (w.action === WindowAction.LAPSED) {
    return "lapsed";
  }
  if (!w.closesAt || w.closesAt.getTime() < nowMs) {
    return "closed";
  }
  return null;
};

export const CHALLENGE_WINDOW_BLOCK_COPY: Record<ChallengeWindowBlock, string> = {
  none: "There is no open review window on this covenant. A challenge can be raised in the next one, which opens on the next verification.",
  closed:
    "The review window has closed. A challenge can be raised in the next one, which opens on the next verification.",
  taken: "This review window has already been challenged. Only one challenge may take a window.",
  lapsed: "This review window lapsed without an independent review. A challenge can be raised in the next one.",
};

// EcoChallenge.hasOpenChallenge(tokenId) — true while a challenge on the covenant is OPEN,
// RESPONDED or SEATED. Until the next contract release, a re-verification while one is open opens
// a fresh window and wipes that challenge's record from the current one (Ravi's review, finding
// 4), so Verify hides itself on this.
export const hasOpenChallenge = async (tokenId: bigint | number | string): Promise<boolean> =>
  Boolean(await challengeReader().hasOpenChallenge(BigInt(tokenId)));

export const isLiveChallengeState = (state: ChallengeState) =>
  state === ChallengeState.OPEN || state === ChallengeState.RESPONDED || state === ChallengeState.SEATED;
const isLive = isLiveChallengeState;

// Every challenge ever raised against a token, newest first, within the capped scan (see
// findOpenChallenge for why a scan). Callers derive both "the live one" and "the last determined
// one" from a single pass — the release-after-re-verification fallback needs the DETERMINED
// challenge's id, which nothing on the token points at once a fresh window has opened.
export const listChallengesFor = async (
  tokenId: bigint | number | string,
  scanLimit = 60
): Promise<ChallengeRecord[]> => {
  const token = BigInt(tokenId);
  const challenge = challengeReader();
  const next = Number(await challenge.nextChallengeId());
  const floor = Math.max(0, next - scanLimit);
  const ids: bigint[] = [];
  for (let id = next - 1; id >= floor; id--) {
    ids.push(BigInt(id));
  }
  const batch = 20;
  const found: ChallengeRecord[] = [];
  for (let i = 0; i < ids.length; i += batch) {
    const records = await Promise.all(ids.slice(i, i + batch).map((id) => readChallenge(id)));
    records.forEach((c) => {
      if (c.subject === token) {
        found.push(c);
      }
    });
  }
  return found;
};

// Every token that has had a challenge raised against it within the capped scan, newest challenge
// first, each id once. This is listChallengesFor turned inside out — one scan back from
// nextChallengeId grouped by `subject` instead of filtered to one — and exists for the Portal's
// term-challenge queue: GET /settlement/term-challenges at backend HEAD is still the V9 frozen-drip
// query (docs/v10/BACKEND_V10_1_BRANCH_AUDIT.md, item 3), so a live OPEN / RESPONDED / SEATED
// challenge, an upheld FIELD finding waiting on a reseat (never freezes, EcoChallenge.sol:805-812)
// and an upheld EVIDENCE finding awaiting a better record are all absent from the REST rows.
// Nothing on chain enumerates "tokens under review", but the challenge ids are a dense counter
// (nextChallengeId starts at 1, EcoChallenge.sol:286,326) so the tail of the log IS the recent
// review set. The caller reads each subject's full state (fetchCovenantReviewState) and keeps
// what still stands — this only says where to look. Not filtered by state on purpose: a
// DETERMINED challenge is exactly the one whose consequence may still be holding the token.
// Same batching and cap as listChallengesFor; id 0 is read and ignored (subject 0, never minted).
export const listRecentChallengeSubjects = async (scanLimit = 200): Promise<bigint[]> => {
  const challenge = challengeReader();
  const next = Number(await challenge.nextChallengeId());
  const floor = Math.max(0, next - scanLimit);
  const ids: bigint[] = [];
  for (let id = next - 1; id >= floor; id--) {
    ids.push(BigInt(id));
  }
  const batch = 20;
  // Insertion order is scan order (newest challenge first), and a Set dedupes a token that has
  // been challenged more than once without losing that ordering.
  const subjects = new Set<bigint>();
  for (let i = 0; i < ids.length; i += batch) {
    const records = await Promise.all(ids.slice(i, i + batch).map((id) => readChallenge(id)));
    records.forEach((c) => {
      // The struct is zero-filled for an id that was never issued; NONE is the tell. (BigInt(0)
      // rather than a literal — the tsconfig target predates them, as noted in findOpenChallenge.)
      if (c.state !== ChallengeState.NONE && c.subject > BigInt(0)) {
        subjects.add(c.subject);
      }
    });
  }
  return Array.from(subjects);
};

// The challenge currently standing against a token, or null. Two sources, in order:
//   1. windowOf(tokenId).challengeId — set when a challenge took the open review window. This is
//      always the case for a deed challenge (it has to, to hold the settlement).
//   2. a scan back from nextChallengeId — a verification-stage challenge may be raised OUTSIDE
//      any window (the contract does not require one), and then nothing on the token points at
//      it. Ids are dense and small on testnet; the scan is capped so it cannot run away.
// The window record survives finalisation, so (1) is checked for liveness rather than trusted.
export const findOpenChallenge = async (
  tokenId: bigint | number | string,
  scanLimit = 200
): Promise<ChallengeRecord | null> => {
  const token = BigInt(tokenId);
  const challenge = challengeReader();

  const w = await readWindow(token);
  if (w.action === WindowAction.CHALLENGED) {
    const c = await readChallenge(w.challengeId);
    if (c.subject === token && isLive(c.state)) {
      return c;
    }
  }

  // Plain numbers: ids are dense counters and the scan is capped, so no precision concern, and
  // the tsconfig target predates BigInt literals.
  const next = Number(await challenge.nextChallengeId());
  const floor = Math.max(0, next - scanLimit);
  const batch = 20;
  for (let hi = next; hi > floor; hi -= batch) {
    const lo = Math.max(floor, hi - batch);
    const ids: bigint[] = [];
    for (let id = hi - 1; id >= lo; id--) {
      ids.push(BigInt(id));
    }
    const records = await Promise.all(ids.map((id) => readChallenge(id)));
    const hit = records.find((c) => c.subject === token && isLive(c.state));
    if (hit) {
      return hit;
    }
  }
  return null;
};

// ---------------------------------------------------------------------------------------------
// Signing
// ---------------------------------------------------------------------------------------------

const CHALLENGE_DOMAIN: ethers.TypedDataDomain = {
  name: "EcoChallenge",
  version: "1",
  chainId: MainChainID,
  verifyingContract: challengeContractAddress,
};
const VERDICT_TYPES: Record<string, ethers.TypedDataField[]> = {
  Verdict: [
    { name: "challengeId", type: "uint256" },
    { name: "upheld", type: "bool" },
    { name: "inBreach", type: "bool" },
    { name: "nonce", type: "uint256" },
  ],
};

const GOVERNANCE_DOMAIN: ethers.TypedDataDomain = {
  name: "EcoFuturesGovernance",
  version: "1",
  chainId: MainChainID,
  verifyingContract: governanceContractAddress,
};
const PREMINT_TYPES: Record<string, ethers.TypedDataField[]> = {
  PreMintVerdict: [
    { name: "requestId", type: "uint256" },
    { name: "ground", type: "uint8" },
    { name: "upheld", type: "bool" },
    { name: "nonce", type: "uint256" },
  ],
};

export interface CollectedSignature {
  signer: string;
  signature: string;
}

// What a dialog needs to collect and submit one determination, independent of which contract
// judges it. Built by verdictSpec / preMintSpec below and consumed by usePanelVerdict.
export interface VerdictSpec {
  // Allowed signers, as the contract will check them. One entry for a single checker.
  seats: string[];
  // Signatures required: 2 for a panel of three, 1 for a checker.
  threshold: number;
  // The digest as the contract computes it RIGHT NOW (nonce re-read). Used to attribute a pasted
  // signature to a seat and to make sure a wallet-signed one matches what the chain expects.
  digest: () => Promise<string>;
  // Sign a fresh digest with the connected wallet. MetaMask shows the typed struct, not a hash.
  sign: (signer: ethers.Signer) => Promise<string>;
  // Send the determination; resolves with the tx hash once mined. Throws on revert.
  submit: (signatures: string[]) => Promise<string>;
}

// A typed-data signature is only worth collecting if it hashes to the digest the contract will
// verify against. Comparing our local EIP-712 hash to the contract's view catches a domain or
// typehash drift (a redeploy with a different name/version, say) at signing time instead of as an
// InsufficientPanelSignatures revert after two people have signed.
const assertDigestMatches = (local: string, onChain: string, what: string) => {
  if (local.toLowerCase() !== onChain.toLowerCase()) {
    throw new Error(
      `${what}: the signing domain does not match the deployed contract — check the EIP-712 domain in challenge.service.ts`
    );
  }
};

export const verdictSpec = async (challengeId: bigint, upheld: boolean, inBreach: boolean): Promise<VerdictSpec> => {
  const challenge = challengeReader();
  const record = await readChallenge(challengeId);
  const seats = record.panelPath ? record.panel : [record.panel[0]];
  const threshold = record.panelPath ? 2 : 1;

  const value = async () => ({
    challengeId,
    upheld,
    inBreach,
    nonce: BigInt(await challenge.judgeNonce(challengeId)),
  });

  const digest = async () => {
    const v = await value();
    const local = ethers.TypedDataEncoder.hash(CHALLENGE_DOMAIN, VERDICT_TYPES, v);
    const onChain: string = await challenge.verdictDigest(challengeId, upheld, inBreach);
    assertDigestMatches(local, onChain, "Verdict");
    return onChain;
  };

  return {
    seats: seats.filter((s) => s !== ZERO_ADDRESS),
    threshold,
    digest,
    sign: async (signer) => {
      // Re-read the nonce immediately before signing — see the header.
      const v = await value();
      await digest();
      return signer.signTypedData(CHALLENGE_DOMAIN, VERDICT_TYPES, v);
    },
    submit: async (signatures) => {
      const signer = await getMetaMaskSigner();
      const writable = getContract("challenge", signer);
      if (!writable) {
        throw new Error("EcoChallenge contract not found");
      }
      const tx = await writable.recordOutcome(challengeId, upheld, inBreach, signatures);
      const receipt = await tx.wait();
      if (!receipt || receipt.status !== 1) {
        throw new Error("recordOutcome transaction failed");
      }
      return tx.hash as string;
    },
  };
};

export const preMintSpec = async (requestId: bigint, ground: PreMintGround, upheld: boolean): Promise<VerdictSpec> => {
  const governance = governanceReader();
  // SCORE draws the three-seat panel deciding by two; DOCUMENTS a single checker.
  const seats: string[] =
    ground === PreMintGround.SCORE
      ? Array.from((await governance.preMintPanel(requestId)) as string[]).map(String)
      : [String(await governance.preMintChecker(requestId))];
  const threshold = ground === PreMintGround.SCORE ? 2 : 1;

  const value = async () => ({
    requestId,
    ground,
    upheld,
    nonce: BigInt(await governance.preMintNonce(requestId)),
  });

  const digest = async () => {
    const v = await value();
    const local = ethers.TypedDataEncoder.hash(GOVERNANCE_DOMAIN, PREMINT_TYPES, v);
    const onChain: string = await governance.preMintDigest(requestId, ground, upheld);
    assertDigestMatches(local, onChain, "Pre-mint verdict");
    return onChain;
  };

  return {
    seats: seats.filter((s) => s !== ZERO_ADDRESS),
    threshold,
    digest,
    sign: async (signer) => {
      const v = await value();
      await digest();
      return signer.signTypedData(GOVERNANCE_DOMAIN, PREMINT_TYPES, v);
    },
    submit: async (signatures) => {
      const signer = await getMetaMaskSigner();
      const writable = getContract("governance", signer);
      if (!writable) {
        throw new Error("Governance contract not found");
      }
      const tx = await writable.judgePreMint(requestId, ground, upheld, signatures);
      const receipt = await tx.wait();
      if (!receipt || receipt.status !== 1) {
        throw new Error("judgePreMint transaction failed");
      }
      return tx.hash as string;
    },
  };
};

// Who signed this, for the digest the contract will check. Throws on malformed input.
export const recoverSigner = (digest: string, signature: string): string => ethers.recoverAddress(digest, signature);

// SigLib walks the array and reverts SignersNotAscending unless each recovered signer is strictly
// greater than the last — so order by address, and drop a duplicate rather than let it revert.
export const orderSignatures = (collected: CollectedSignature[]): string[] => {
  const bySigner = new Map<string, string>();
  collected.forEach((c) => bySigner.set(c.signer.toLowerCase(), c.signature));
  return Array.from(bySigner.entries())
    .sort(([a], [b]) => (BigInt(a) < BigInt(b) ? -1 : 1))
    .map(([, sig]) => sig);
};

// ---------------------------------------------------------------------------------------------
// Server-collected verdicts (V10.1.2 backend, POST/GET /challenges/:id/verdict-signatures)
// ---------------------------------------------------------------------------------------------
//
// Three seats in three places had no way to hand each other a signature except by messaging it,
// so the backend now collects them. Each seat signs the same typed data as before (verdictSpec's
// sign, nonce re-read) and POSTs it; the server recovers the signer against verdictDigest, refuses
// anyone not in getPanel, stores it per (challenge, judgeNonce, upheld, inBreach, signer) — a seat
// may replace its own — and once one verdict reaches the threshold sorts the signatures and queues
// recordOutcome on its challenge worker from the server wallet. Nobody pastes, nobody pays gas.
// A redraw (PanelDrawn) drops the signatures of seats no longer drawn; a recorded outcome clears
// them all (the nonce moved, so none could be reused anyway).

// One verdict's tally. `signers` are lower-case addresses, already filtered to current seats.
export interface VerdictTally {
  upheld: boolean;
  inBreach: boolean;
  count: number;
  signers: string[];
}

export interface VerdictSignatureStatus {
  challengeId: string;
  // Whether the challenge is still SEATED on chain (the backend's own read). Turns false once the
  // server's recordOutcome lands — the signal the UI waits on.
  seated: boolean;
  threshold: number;
  // Lower-case, zero seats already dropped: three for a panel, one for a single checker.
  panel: string[];
  judgeNonce: string;
  verdicts: VerdictTally[];
}

export const fetchVerdictSignatures = async (challengeId: bigint | string): Promise<VerdictSignatureStatus> => {
  const axios = new AxiosFactory().axios;
  const data = await axios.get(ApiRoutes.challenges.verdictSignatures(String(challengeId))).then((res) => res.data);
  return {
    challengeId: String(data?.challengeId ?? challengeId),
    seated: !!data?.seated,
    threshold: Number(data?.threshold ?? 0),
    panel: (Array.isArray(data?.panel) ? data.panel : []).map((a: string) => String(a).toLowerCase()),
    judgeNonce: String(data?.judgeNonce ?? "0"),
    verdicts: (Array.isArray(data?.verdicts) ? data.verdicts : []).map((v: any) => ({
      upheld: !!v.upheld,
      inBreach: !!v.inBreach,
      count: Number(v.count ?? 0),
      signers: (Array.isArray(v.signers) ? v.signers : []).map((a: string) => String(a).toLowerCase()),
    })),
  };
};

// Sign this verdict with the connected wallet and hand the signature to the server. Throws if the
// wallet holds no seat (its signature would be refused 403 anyway), on a MetaMask rejection (the
// caller checks isUserRejection), and on any refusal by the server (verdictSignatureErrorCopy).
export const signAndSubmitVerdict = async (
  challengeId: bigint,
  upheld: boolean,
  inBreach: boolean
): Promise<VerdictTally[]> => {
  const spec = await verdictSpec(challengeId, upheld, inBreach);
  const signer = await getMetaMaskSigner();
  const address = (await signer.getAddress()).toLowerCase();
  if (!spec.seats.some((s) => s.toLowerCase() === address)) {
    throw new Error("The connected wallet does not hold a seat on this panel, so its signature would not count.");
  }
  const signature = await spec.sign(signer);
  const axios = new AxiosFactory().axios;
  const data = await axios
    .post(ApiRoutes.challenges.verdictSignatures(challengeId.toString()), { upheld, inBreach, signature })
    .then((res) => res.data);
  return Array.isArray(data?.verdicts) ? data.verdicts : [];
};

// The server's refusals, as a sentence. 400 is the challenge not being SEATED (seated too early,
// or decided / redrawn since the page loaded) or a signature it could not parse; 403 is a signer
// outside getPanel — usually a redraw that replaced this seat. Null when it is not a server
// refusal (network, MetaMask), so the caller can fall back to describeChainError.
export const verdictSignatureErrorCopy = (e: any): string | null => {
  const status: number | undefined = e?.response?.status;
  const message = String(e?.response?.data?.message ?? "");
  if (status === 403) {
    return (
      "Your wallet is not seated on this challenge's panel. If the panel was redrawn, the new seats sign — " +
      "reload to see who they are."
    );
  }
  if (status === 400 && /not awaiting a panel decision/i.test(message)) {
    return (
      "This challenge is no longer waiting for a panel decision — it may have been decided or redrawn since you " +
      "opened it. Reload to see where it stands."
    );
  }
  if (status === 400 && /malformed signature/i.test(message)) {
    return "The server could not read that signature. Sign again.";
  }
  if (status === 401) {
    return "Your session has expired. Sign in again, then sign the verdict.";
  }
  return null;
};

// True when the verdict-signature endpoint cannot be used at all — no response (network, CORS,
// the API down), a 5xx, or a 404 from a backend that predates it. The dialogs then fall back to
// collecting signatures by hand and submitting recordOutcome directly.
export const verdictEndpointUnavailable = (e: any): boolean => {
  const status: number | undefined = e?.response?.status;
  return !e?.response || status === 404 || (typeof status === "number" && status >= 500);
};

// ---------------------------------------------------------------------------------------------
// Server-collected pre-mint verdicts (POST/GET /pre-mint-challenges/:id/verdict-signatures)
// ---------------------------------------------------------------------------------------------
//
// The pre-mint counterpart of the block above: each judge signs the same typed data as
// preMintSpec.sign (nonce re-read) and POSTs it; the server recovers the signer against
// preMintDigest, refuses anyone not in panelFor(requestId, ground), stores it per (requestId,
// nonce, ground, upheld, signer) — a judge may replace their own — and once one verdict reaches
// the ground's threshold queues judgePreMint on the challenge worker from the server wallet. A
// PreMintDetermined event (processHandlePreMintDetermined) drops the now-unusable signatures.
//
// Unlike the post-mint lane this is grouped by GROUND, not by a single panel: SCORE draws a
// 3-seat panel deciding by 2, DOCUMENTS a single checker, each with its own panel and threshold.

export interface PreMintVerdictTally {
  ground: PreMintGround;
  upheld: boolean;
  count: number;
  signers: string[];
}

export interface PreMintGroundStatus {
  ground: PreMintGround;
  threshold: number;
  // Lower-case, zero seats already dropped: three for the SCORE panel, one for the DOCUMENTS checker.
  panel: string[];
  verdicts: PreMintVerdictTally[];
}

export interface PreMintVerdictSignatureStatus {
  requestId: string;
  nonce: string;
  byGround: PreMintGroundStatus[];
}

const toPreMintTally = (ground: PreMintGround, v: any): PreMintVerdictTally => ({
  ground,
  upheld: !!v?.upheld,
  count: Number(v?.count ?? 0),
  signers: (Array.isArray(v?.signers) ? v.signers : []).map((a: string) => String(a).toLowerCase()),
});

export const fetchPreMintVerdictSignatures = async (
  requestId: bigint | string
): Promise<PreMintVerdictSignatureStatus> => {
  const axios = new AxiosFactory().axios;
  const data = await axios
    .get(ApiRoutes.preMintChallenges.verdictSignatures(String(requestId)))
    .then((res) => res.data);
  return {
    requestId: String(data?.requestId ?? requestId),
    nonce: String(data?.nonce ?? "0"),
    byGround: (Array.isArray(data?.byGround) ? data.byGround : []).map((g: any) => {
      const ground = Number(g?.ground) as PreMintGround;
      return {
        ground,
        threshold: Number(g?.threshold ?? 0),
        panel: (Array.isArray(g?.panel) ? g.panel : []).map((a: string) => String(a).toLowerCase()),
        verdicts: (Array.isArray(g?.verdicts) ? g.verdicts : []).map((v: any) => toPreMintTally(ground, v)),
      };
    }),
  };
};

// Sign this ground's verdict with the connected wallet and hand the signature to the server.
// Throws if the wallet holds no seat for this ground (its signature would be refused 403 anyway),
// on a MetaMask rejection (the caller checks isUserRejection), and on any refusal by the server
// (preMintVerdictSignatureErrorCopy).
export const signAndSubmitPreMintVerdict = async (
  requestId: bigint,
  ground: PreMintGround,
  upheld: boolean
): Promise<PreMintVerdictTally[]> => {
  const spec = await preMintSpec(requestId, ground, upheld);
  const signer = await getMetaMaskSigner();
  const address = (await signer.getAddress()).toLowerCase();
  if (!spec.seats.some((s) => s.toLowerCase() === address)) {
    throw new Error(
      "The connected wallet is not drawn as a judge for this ground, so its signature would not count."
    );
  }
  const signature = await spec.sign(signer);
  const axios = new AxiosFactory().axios;
  const data = await axios
    .post(ApiRoutes.preMintChallenges.verdictSignatures(requestId.toString()), { ground, upheld, signature })
    .then((res) => res.data);
  return (Array.isArray(data?.verdicts) ? data.verdicts : []).map((v: any) => toPreMintTally(ground, v));
};

// The server's refusals, as a sentence. 400 is the request having no open pre-mint challenge (it
// may have been judged, or the request was never challenged) or a signature it could not parse;
// 403 is a signer outside panelFor(requestId, ground) — usually a TA roster change or GTA freeze.
// Null when it is not a server refusal (network, MetaMask), so the caller can fall back to
// describeChainError. verdictEndpointUnavailable (above) covers the "endpoint is down" case for
// both lanes — nothing pre-mint-specific about that one.
export const preMintVerdictSignatureErrorCopy = (e: any): string | null => {
  const status: number | undefined = e?.response?.status;
  const message = String(e?.response?.data?.message ?? "");
  if (status === 403) {
    return (
      "Your wallet is not drawn as a judge for this ground. If the country's Trust Admin roster or the GTA " +
      "registry changed since this challenge was raised, reload to see who is drawn now."
    );
  }
  if (status === 400 && /no open pre-mint challenge/i.test(message)) {
    return (
      "This request has no open pre-mint challenge any more — it may have been determined since you opened " +
      "this. Reload to see where it stands."
    );
  }
  if (status === 400 && /malformed signature/i.test(message)) {
    return "The server could not read that signature. Sign again.";
  }
  if (status === 401) {
    return "Your session has expired. Sign in again, then sign the verdict.";
  }
  return null;
};

// ---------------------------------------------------------------------------------------------
// Raising
// ---------------------------------------------------------------------------------------------

export interface RaiseChallengeResult {
  txHash: string;
  challengeId: bigint | null;
}

// EcoChallenge.raiseChallenge(tokenId, kind, evidence) — direct wallet tx (see header). If a
// review window is open the challenge takes it (superseding an attestation already made in it);
// a deed-stage one also holds the settlement. Reverts NotIndependentChallenger for the easement's
// own verifier, its guardian, a same-org peer or anyone admitted by the same holder, and
// AlreadyChallenged for a wallet that has challenged this token before — one challenge per
// (token, challenger), ever.
export const raiseChallenge = async (
  tokenId: bigint | number | string,
  kind: ChallengeKind,
  evidence: string
): Promise<RaiseChallengeResult> => {
  const signer = await getMetaMaskSigner();
  const writable = getContract("challenge", signer);
  if (!writable) {
    throw new Error("EcoChallenge contract not found");
  }
  // Preflight on the plain read-only provider. connect-evm answers the wallet's own estimateGas
  // with null on a revert, dropping the revert data, so NotIndependentChallenger / AlreadyChallenged
  // would otherwise surface as "invalid BigNumberish value" with nothing describeChainError can name.
  await challengeReader().raiseChallenge.staticCall(BigInt(tokenId), kind, evidence, {
    from: await signer.getAddress(),
  });
  const tx = await writable.raiseChallenge(BigInt(tokenId), kind, evidence);
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) {
    throw new Error("raiseChallenge transaction failed");
  }
  let challengeId: bigint | null = null;
  for (const log of receipt.logs ?? []) {
    try {
      const parsed = writable.interface.parseLog({ topics: Array.from(log.topics), data: log.data });
      if (parsed?.name === "ChallengeRaised") {
        challengeId = BigInt(parsed.args[0]);
        break;
      }
    } catch {
      // a log from another contract in the same receipt (EcoBank's DeedChallenged, say)
    }
  }
  return { txHash: tx.hash as string, challengeId };
};

export const hasChallenged = async (tokenId: bigint | number | string, wallet: string): Promise<boolean> =>
  Boolean(await challengeReader().hasChallenged(BigInt(tokenId), wallet));

// One direct EcoChallenge write, mined and checked. Every action below is the same three lines
// around a different method, so they share this rather than each carrying its own copy.
const sendChallengeTx = async (method: string, args: unknown[]): Promise<string> => {
  const signer = await getMetaMaskSigner();
  const writable = getContract("challenge", signer);
  if (!writable) {
    throw new Error("EcoChallenge contract not found");
  }
  const tx = await writable[method](...args);
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) {
    throw new Error(`${method} transaction failed`);
  }
  return tx.hash as string;
};

// EcoChallenge.attest(tokenId) — the first-to-act alternative to challenging, inside the open
// review window. Same independence gate as raiseChallenge (§2.2: an attestor carries the
// verifier's finding, so the liability attaches to them too). Resets the covenant's run of
// unattested windows — three in a row halt fund release (§10), which is why this exists at all.
// Distinct from EcoBank.attestDeed: that one attests the DEED during settlement and goes through
// the relay; this one attests a VERIFICATION and is a direct tx (EcoChallenge reads msg.sender).
export const attest = (tokenId: bigint | number | string): Promise<string> =>
  sendChallengeTx("attest", [BigInt(tokenId)]);

// The challenged verifier's one response (§4). Only c.verifier may call it, only while OPEN.
// Silence is allowed and does not decide the outcome; this exists so it is at least possible.
export const respondToChallenge = (challengeId: bigint, evidence: string): Promise<string> =>
  sendChallengeTx("respondToChallenge", [challengeId, evidence]);

// §10 — the challenger withdraws before a panel is drawn (OPEN or RESPONDED). No flag; a held
// deed settlement is released exactly as a dismissal would.
export const withdrawChallenge = (challengeId: bigint): Promise<string> =>
  sendChallengeTx("withdrawChallenge", [challengeId]);

// Anyone may draw the panel once the response window has passed (or the verifier has responded).
// A backend job is meant to do this; the dialogs offer it as a manual fallback, like finalise.
export const seatPanel = (challengeId: bigint): Promise<string> => sendChallengeTx("seatPanel", [challengeId]);

// §6 — redraw the panel ONCE if it failed to reach two signatures within panelWindow of seating.
// Permissionless; reverts PanelWindowOpen before then and AlreadyRedrawn after.
export const redrawPanel = (challengeId: bigint): Promise<string> => sendChallengeTx("redrawPanel", [challengeId]);

// Release a held settlement / unfreeze the drip once the covenant has genuinely been re-verified:
// requires verificationsPerformed > verificationsAtFreeze, else NotReVerified. Permissionless —
// a backend job normally runs it; offered as a manual fallback like seatPanel.
export const releaseAfterReVerification = (tokenId: bigint | number | string, challengeId: bigint): Promise<string> =>
  sendChallengeTx("releaseAfterReVerification", [BigInt(tokenId), challengeId]);

// The GTA registry is the authority on who is a GTA (V10_Accounts_Required §3); the first holder
// is the legacy single GTA and "not a substitute for the registry". Read-only, no popup.
export const isGTA = async (address: string): Promise<boolean> => Boolean(await governanceReader().isGTA(address));

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

// Human copy for the custom errors a user can actually hit from these flows. Anything not listed
// falls back to the decoded error name, which is still better than a selector.
const ERROR_COPY: Record<string, string> = {
  PanelUnfillable:
    "A panel cannot be seated yet — the GTA registry needs at least three members before any challenge " +
    "can be decided. This is not something you did.",
  InsufficientPanelSignatures:
    "Not enough valid panel signatures. A determination has probably been recorded since these were signed " +
    "(the nonce moved) — collect fresh signatures.",
  PreMintSignatures:
    "Not enough valid signatures for this ground. A determination may have been recorded since these were " +
    "signed — collect fresh signatures.",
  SignersNotAscending: "Signatures were submitted out of order — this is a client bug, please report it.",
  NotIndependentChallenger:
    "Your wallet is not independent of this verification: you must be a registered verifier from a different " +
    "organisation, admitted by a different Trust Admin, and not the easement's verifier or guardian.",
  AlreadyChallenged: "This wallet has already challenged this covenant. Each wallet may challenge a token once.",
  WindowAlreadyTaken: "This review window has already been acted on.",
  WindowClosed: "The review window has closed.",
  NoOpenWindow: "There is no open review window on this token.",
  InvalidChallengeState: "The challenge is not in a state that allows this — reload and check its status.",
  ResponseWindowOpen: "The challenged verifier's response window is still open. The panel can be drawn once it closes.",
  NotTheChallengedVerifier: "Only the challenged verifier can respond.",
  NotReVerified: "The covenant has not been re-verified since the freeze.",
  PreMintNotVerified: "This request has not been verified yet, so there is nothing to judge.",
  PreMintScore: "A ground must be selected.",
  // Fired from ClaimFundNow via verifyEasement, from Tree boosts, and from the release fallback.
  DripFrozen:
    "This covenant's TREE rewards are frozen after a breach finding. A real re-verification, followed by " +
    "release, is the only thing that unfreezes them.",
  InvalidKind: "Select whether this is a field or an evidence challenge.",
  NotAuthorised:
    "Your wallet is not allowed to do this — only the challenger can withdraw, only the verifier can respond.",
  AlreadyResponded: "The challenged verifier has already responded. One response, no revision.",
  PanelWindowOpen: "The panel's signing window is still open. It can be redrawn once that window lapses.",
  AlreadyRedrawn: "This panel has already been redrawn once, which is the limit.",
  WindowStillOpen: "The review window is still open.",
  // V10 §10: three consecutive review windows nobody attested or challenged, or an upheld evidence
  // finding with no better record filed since. Cured by the next verification.
  ReleasesHalted:
    "Fund release is halted on this covenant: its review windows went unattested three times in a row, or an " +
    "upheld evidence challenge is awaiting a better record. The next verification clears it.",
  // EcoBank: the attestor must re-attest every N verification cycles or ClaimFundNow refuses.
  AttestOverdue:
    "The deed attestor has not re-attested this covenant within the required number of verification cycles. " +
    "They must re-attest before funds can be released.",
  // EcoBank L2 (V10): an EXISTING selector with a NEW meaning — it used to be 500 × (years + 1).
  StartingPriceTooLow:
    "The starting price is below this covenant's protocol price floor. The floor is what makes the 1% review " +
    "pool cover the covenant's reviews for its whole term — list at or above the floor shown.",
  Paused: "TREE rewards are paused by the protocol right now. Try again once they resume.",
  ChallengeIsOpen: "A challenge is open on this covenant. This action waits until it is determined.",
  EasementIsChallenged:
    "This verification is under a pre-mint challenge, so it cannot be minted until that is decided.",
  TooManyOffers: "This EFT already has the maximum of 50 open offers. Wait for one to be withdrawn or accepted.",

  // ---- EcoBank settlement (attest / anchor / finalise / reAttest) ----
  NotIndependentAttestor:
    "Your wallet cannot attest this deed: an attestor must be a registered verifier in the covenant's country from a " +
    "different organisation than its verifier, or a Trust Admin once the withhold window has passed.",
  MaxAttestorsReached: "This deed already has the maximum number of attestors.",
  NotBoundVerifier: "Only the covenant's own verifier can anchor the deed.",
  DeadlinePassed:
    "The deed could not be anchored: the activation window has passed, or the deed date is before the day KYC was " +
    "confirmed or in the future. If the window has passed, the server will cancel the settlement and refund the patron.",
  FinaliseWindowNotReached: "The attest-withhold window has not closed yet. Finalisation opens once it does.",
  InvalidSettlementState: "The settlement is not in the state this action needs — reload and check its progress.",
  NotAttestor: "Only the wallet that attested this deed can re-attest it. If the attest slot was forfeited, nobody can.",
  ActivationWindowNotLapsed: "The activation window is still open, so the settlement cannot be cancelled yet.",

  // ---- EcoBank auction / listing ----
  AuctionConflict: "An auction already exists for this EFT, or an expired one still has bidders and must be ended first.",
  EasementNFTNotApproved: "The EFT has not been approved to the Bank. Approve it, then list again.",
  TokenNotEligibleForPrimaryMarket: "This EFT has already completed its primary sale; it can only be listed for offers.",
  DurationTooShort: "The auction must run for at least one minute.",
  InsufficientBalance: "Your balance or allowance is below the bid amount.",
  BidTooLow: "Your bid is below the current highest bid.",
  BidderNotAllowed: "The seller, the guardian and registered verifiers cannot bid on this EFT.",
  NotGuardian: "Only a guardian can list an EFT.",
  GuardianMismatched: "Only the covenant's guardian can list it.",
  EasementOwnerMismatched: "You do not own this EFT.",

  // ---- EcoFuturesCore ----
  NotYetVerified: "This request has not been verified yet, so it cannot be challenged.",
  InvalidEasement: "No such covenant or request, or it has already been fulfilled.",
  EasementIsCancelled: "This covenant has been cancelled.",
  NotEasementVerifier: "Only the covenant's seated verifier can do this.",
  VerifierIsSuspended: "Your verifier wallet is suspended.",
  MissingRequiredRole: "Your wallet does not hold the role this action needs.",
  NotSigningHolder:
    "Only the Trust Admin who admitted this covenant's verifier — or a Global Trust Admin — can act on it.",
  HolderIsFrozen: "Your Trust Admin wallet is frozen by governance.",
  NewVerifierIsDismissed: "That verifier has been dismissed and cannot be seated.",
  NoDismissalOrVerificationDelays:
    "The seated verifier must be dismissed (or overdue on verification) before the covenant can be reseated. " +
    "Dismiss first, then reassign.",
  VerifierMismatch: "The replacement verifier must be admitted by the same Trust Admin that sponsors this covenant.",
  SameOrganisation: "The replacement verifier must come from a different organisation than the one being replaced.",
  VerifierSignatureVerificationFailed: "The new verifier's signature did not verify — ask them to sign again.",
  SignatureHasExpired: "The new verifier's signature has expired — ask them to sign again.",
  AlreadyBlocked: "This covenant is already blocked.",
  NotBlocked: "This covenant is not blocked, so it cannot be cancelled or unblocked.",
  CannotCancel: "This covenant cannot be cancelled: it must be blocked first and not already cancelled.",
  MaxBlockingLimitReached: "This covenant has already been blocked the maximum number of times in this window.",
  AllVerificationsCompleted: "Every verification for this covenant has been performed.",
  // V10.1.2: after the sale, verifyEasement releases an instalment through ClaimFundNow, which
  // refuses until one is due (easementVestingStartTime + (releasedCount + 1) × releaseFundInterval).
  CannotClaimYet:
    "No instalment is due on this covenant yet, so it cannot be re-verified now. Verification opens when the next " +
    "one falls due.",
  // V10.1.2: EFTs cannot be burnt at all.
  BurnDisabled: "EFTs cannot be burnt.",
  InvalidEcoScore: "The EcoScore must be between 1 and 100.",
  InvalidCovenant: "The covenant's start and target bands must both be set.",

  // ---- EcoFuturesAdmin ----
  NotTheBoundVerifier: "Only the verifier bound to this request can do this.",
  AlreadyFlagged: "Your wallet has already flagged this verifier.",
  InsufficientFlags: "This verifier has not reached the dismissal bar yet.",
  NotHolder:
    "The Trust Admin who admitted this verifier cannot dismiss or suspend them — another Trust Admin or a Global " +
    "Trust Admin must.",
  NotRegisteredVerifier: "That wallet is not a registered verifier.",
  AlreadySuspended: "This verifier is already suspended (or your wallet is).",
  NotSuspended: "This verifier is not suspended.",
  NotSigningAdmin: "Only the Trust Admin who suspended this verifier — or a Global Trust Admin — can lift it.",
  // addHolder (the Portal's "Approve Admin" grant). InvalidCountryCode is a deployment gap, not a
  // user error: setValidCountryCodes is DEFAULT_ADMIN_ROLE-only, so the UI can only name it.
  InvalidCountryCode:
    "This country is not enabled on the EcoFuturesAdmin contract yet (setValidCountryCodes has not been run for " +
    "it), so no Trust Admin can be granted for it. The contract admin must enable the country code first.",
  AlreadyHolder: "This wallet is already a Trust Admin.",
  // V10.1.2 admission guards.
  GTACannotBeHolder:
    "That wallet is a Global Trust Admin in the GTA registry, and a GTA cannot also be admitted as a Trust Admin.",
  AlreadyVerifier: "That wallet is already a registered verifier, so it cannot be admitted again.",
  // V10.1.2: releasing a request's verification funds waits while a pre-mint challenge on it stands.
  ChallengeOpen:
    "A pre-mint challenge is open on this verification. Its funds cannot be released until the challenge is decided.",
  // V10.1.2: a type 4 (deed-check hold) release after the mint now comes back AlreadyFulfilled.
  AlreadyFulfilled:
    "This verification request has already been fulfilled — the covenant is minted, so this action no longer applies.",
  OnlyGlobalAdmin: "Only the Global Trust Admin can grant the Trust Admin role.",
  GlobalAdminExists: "There is already a Global Trust Admin; only one is allowed.",
  MaxCountryAdminsReached: "This country already has the maximum number of Trust Admins.",
  InvalidOrgID:
    "The organisation ID is not usable on-chain: it is missing, already taken by another Trust Admin or verifier " +
    "org, or does not match the inviter's organisation.",

  // ---- EcoChallenge review pool ----
  PoolAlreadyClaimed: "The review pool has already been paid for this challenge.",
  NoPoolToClaim: "There is nothing left in this covenant's review pool.",
  TermNotOver: "The covenant's term has not ended, so its review pool cannot be swept yet.",
  PanelMayStillRedraw: "The panel may still be redrawn once; the challenge cannot be expired before that.",

  // ---- Tree rewards ----
  NotOwnerOrGuardian: "Only the EFT's owner or its guardian can claim its rewards.",
  OnlyGuardianWhileListed: "While the EFT is listed, only the guardian can claim.",
  RedeemIntervalNotMet: "No reward interval has completed since the last claim.",
  NoEligiblePatronReward: "No patron reward is claimable yet.",
  NoEligibleGuardianReward: "No guardian reward is claimable yet.",
  EasementBlocked: "Rewards cannot be claimed while the covenant is blocked.",
  RewardNotSet: "This EFT's reward has not been set — the settlement has not finalised.",
  MaxSupplyReached: "The TREE supply cap has been reached.",
  // ---- Secondary-market listings (EcoFuturesToken) ----
  // NoActiveListing is what a stale marketplace record produces: the backend still says
  // "opened" but the contract has no listing — after the V10.1 fresh deploy, every listing
  // recorded before it reads this way.
  NoActiveListing:
    "There is no open listing for this EFT on-chain, so there is nothing to cancel. If it still shows as " +
    "listed here, the record is stale — refresh, or re-list it and cancel that.",
  OnlyOwnerCanCancelListing: "Only the wallet that owns this EFT can cancel its listing.",
};

// Every custom-error selector the V10.1 deployment can revert with — 253, keyed by 4-byte
// selector — generated from the handover's errors.json (docs/v10/v10.1/errors.json) rather than
// hand-kept, so a selector the bundled ABIs happen not to carry (a library error such as SigLib's
// PanelUnfillable / SignersNotAscending, which no contract ABI declares) still reads as a name.
// Regenerate `data/v10-error-selectors.json` from errors.json on every deploy. The four V10.1.2
// additions (GTACannotBeHolder, AlreadyVerifier, ChallengeOpen, BurnDisabled) were added from the
// EcoChallenge guide §3c, each checked against keccak256("Name()"), because the V10.1.2 bundle's
// errors.json is not in docs/ yet — regenerate from it once it is.
const SELECTOR_NAMES: Record<string, string> = v10ErrorSelectors as Record<string, string>;

export const errorNameForSelector = (selector: string): string | null =>
  SELECTOR_NAMES[selector.toLowerCase()] ?? null;

// Every contract a user-facing revert can originate from — a revert from a NESTED call decodes
// only against that contract's interface (Tree's Paused via redeemReward, EcoBank's ReleasesHalted
// via Core.verifyEasement, and so on), hence the full sweep. Used for ARGUMENTS: the name itself
// comes from the errors.json map above, which covers selectors no ABI declares.
const ifaces = (): ethers.Interface[] =>
  [
    "challenge",
    "governance",
    "ecofutures-bank",
    "ecofutures-core",
    "ecofutures-admin",
    "tree-token",
    "ecofutures-token",
    "ecolens",
  ]
    .map((t) => getReadOnlyContract(t as any)?.interface)
    .filter(Boolean) as ethers.Interface[];

// Decode a thrown error to a custom-error name, if it is one. ethers v6 shapes: a CALL_EXCEPTION
// carries `revert.name` when the interface knows the error, and raw `data` otherwise (a revert from
// a *nested* contract decodes against that contract's interface, hence the sweep across all of
// them). A RelayRevertError from the gasless lane carries the backend-decoded `errorName` and the
// raw `returndata`; when the backend only put the name in the message ("... reverted: AttestOverdue")
// that is caught too. Null when the error is not a protocol revert (MetaMask rejection, RPC, network).
export const chainErrorName = (e: any): string | null => {
  const name: string | undefined = e?.revert?.name ?? e?.errorName;
  if (name) {
    return name;
  }
  const data: string | undefined = e?.data ?? e?.info?.error?.data ?? e?.error?.data ?? e?.returndata;
  if (typeof data === "string" && data.startsWith("0x") && data.length >= 10) {
    // The selector alone names it — errors.json covers every selector the deployment can emit,
    // including the SigLib ones no ABI carries. The interface sweep is only needed for arguments.
    const bySelector = errorNameForSelector(data.slice(0, 10));
    if (bySelector) {
      return bySelector;
    }
    for (const iface of ifaces()) {
      try {
        const parsed = iface.parseError(data);
        if (parsed) {
          return parsed.name;
        }
      } catch {
        // try the next interface
      }
    }
  }
  const msg: string | undefined = e?.message;
  if (typeof msg === "string") {
    // The relay puts the backend-decoded name in the message ("… reverted: AttestOverdue"); a
    // bare selector can turn up there too ("unrecognised error selector 0x82b410dc").
    const sel = msg.match(/0x[0-9a-fA-F]{8}\b/);
    const bySelector = sel ? errorNameForSelector(sel[0]) : null;
    if (bySelector) {
      return bySelector;
    }
    const hit = Object.values(SELECTOR_NAMES).find((k) => new RegExp(`\\b${k}\\b`).test(msg));
    if (hit) {
      return hit;
    }
  }
  return null;
};

// The human sentence for a revert we can explain, or null. For pages that keep their own error
// heuristics (user rejection, insufficient funds, network) and only want ours when the revert is a
// protocol error with copy — otherwise they fall through to what they had.
export const chainErrorCopy = (e: any): string | null => {
  const name = chainErrorName(e);
  return name ? ERROR_COPY[name] ?? null : null;
};

// Always returns something displayable: the copy, else the decoded error name (still better than a
// selector), else the raw message. MetaMask rejections are left for the caller (isUserRejection).
export const describeChainError = (e: any): string => {
  const name = chainErrorName(e);
  if (name) {
    if (ERROR_COPY[name]) {
      return ERROR_COPY[name];
    }
    const data: string | undefined = e?.data ?? e?.info?.error?.data ?? e?.error?.data ?? e?.returndata;
    if (typeof data === "string" && data.startsWith("0x")) {
      // A decoded error with arguments reads better with them: "EasementValidationFailed(3)".
      for (const iface of ifaces()) {
        try {
          if (iface.parseError(data)) {
            return decodeRelayRevert(data, iface);
          }
        } catch {
          // try the next interface
        }
      }
    }
    return name;
  }
  return e?.shortMessage || e?.reason || e?.message || "Transaction failed";
};
